import type { TFunction } from 'i18next'
import type { NavigateFunction } from 'react-router-dom'
import { HIGH_RISK_EXPOSURE_THRESHOLD } from './tradeCalculations'

export type InboxStudioAction = {
  label: string
  primary?: boolean
  onClick: () => void
}

export type InboxStudioMessage = {
  key: string
  subject: string
  preview: string
  body: string
  date: Date | null
  unread: boolean
  category: string
  tone: 'neutral' | 'warning' | 'danger' | 'success' | 'info' | 'purple'
  actions: InboxStudioAction[]
}

export type InboxOpenTrade = {
  id: string
  symbol: string
  long_short: 'long' | 'short'
  entry_date: string | null
  entry_price?: number
  exit_date?: string | null
  stop_loss: number | null
  take_profit: number | null
  current_price?: number | null
  strategy: string | null
  strategy_id: string | null
  [key: string]: unknown
}

type NotificationRow = {
  id: string
  title: string
  body: string | null
  type: string
  read_at: string | null
  created_at: string
  metadata?: Record<string, unknown>
}

export type InboxFeedItem =
  | { kind: 'notification'; data: NotificationRow }
  | { kind: 'missing_data'; data: InboxOpenTrade }
  | { kind: 'sl_reached'; data: InboxOpenTrade }
  | { kind: 'tp_reached'; data: InboxOpenTrade }
  | { kind: 'near_sl'; data: InboxOpenTrade & { distance?: number } }
  | { kind: 'after_exit_review'; data: InboxOpenTrade }
  | { kind: 'add_first_strategy' }
  | { kind: 'high_risk_exposure'; data: { widgetId: string; riskPercent: number; criteria?: string } }

function getMissingDataBodyKey(trade: InboxOpenTrade): string {
  const missingSL = trade.stop_loss == null || trade.stop_loss === 0
  const missingStrategy =
    (!trade.strategy_id || trade.strategy_id === '') &&
    (!trade.strategy || String(trade.strategy).trim() === '')
  if (missingSL && missingStrategy) return 'trading:dashboard.feedMissingDataBodyBoth'
  if (missingSL) return 'trading:dashboard.feedMissingDataBodySL'
  if (missingStrategy) return 'trading:dashboard.feedMissingDataBodyStrategy'
  return 'trading:dashboard.feedMissingDataBodySL'
}

export function feedItemKey(item: InboxFeedItem): string {
  switch (item.kind) {
    case 'notification':
      return `notif-${item.data.id}`
    case 'add_first_strategy':
      return 'add-first-strategy'
    case 'high_risk_exposure':
      return `risk-${item.data.widgetId}`
    default:
      return `${item.kind}-${item.data.id}`
  }
}

export function buildInboxStudioMessage(
  item: InboxFeedItem,
  t: TFunction,
  navigate: NavigateFunction,
  handlers: {
    openTrade: (trade: InboxOpenTrade) => void
    openTradeById: (tradeId: string) => void
    openTradeReview: (trade: InboxOpenTrade) => void
    editTrade: (trade: InboxOpenTrade) => void
    quickAction: (trade: InboxOpenTrade, kind: 'sltp' | 'fullExit' | 'partialClose') => void
  }
): InboxStudioMessage {
  const fmtPrice = (value: number | null | undefined) =>
    value != null && Number.isFinite(value) ? Number(value).toFixed(2) : '—'

  if (item.kind === 'notification') {
    const n = item.data
    const actions: InboxStudioAction[] = []
    const tradeId = typeof n.metadata?.trade_id === 'string' ? n.metadata.trade_id : null
    if (tradeId) {
      actions.push({
        label: t('trading:dashboard.feedActionViewTrade'),
        primary: true,
        onClick: () => handlers.openTradeById(tradeId),
      })
    }
    return {
      key: feedItemKey(item),
      subject: n.title,
      preview: n.body?.slice(0, 120) || t('trading:inboxStudio.notificationPreview'),
      body: n.body || t('trading:inboxStudio.notificationBodyFallback'),
      date: new Date(n.created_at),
      unread: !n.read_at,
      category: t('trading:inboxStudio.categoryAlert'),
      tone: 'neutral',
      actions,
    }
  }

  if (item.kind === 'sl_reached' || item.kind === 'tp_reached') {
    const isSl = item.kind === 'sl_reached'
    const trade = item.data
    return {
      key: feedItemKey(item),
      subject: t(
        isSl ? 'trading:dashboard.feedStopLossReachedTitle' : 'trading:dashboard.feedTakeProfitReachedTitle',
        { symbol: trade.symbol }
      ),
      preview: t(
        isSl ? 'trading:dashboard.feedStopLossReachedBody' : 'trading:dashboard.feedTakeProfitReachedBody',
        {
          stopLoss: fmtPrice(trade.stop_loss),
          takeProfit: fmtPrice(trade.take_profit),
          currentPrice: fmtPrice(trade.current_price),
        }
      ),
      body: t('trading:inboxStudio.slTpReachedBody', {
        symbol: trade.symbol,
        level: isSl ? fmtPrice(trade.stop_loss) : fmtPrice(trade.take_profit),
        price: fmtPrice(trade.current_price),
        side: trade.long_short,
      }),
      date: trade.entry_date ? new Date(trade.entry_date) : null,
      unread: true,
      category: isSl ? t('trading:inboxStudio.categoryRisk') : t('trading:inboxStudio.categoryTarget'),
      tone: isSl ? 'danger' : 'success',
      actions: [
        {
          label: t('trading:dashboard.feedActionViewTrade'),
          primary: true,
          onClick: () => handlers.openTrade(trade),
        },
        {
          label: isSl ? t('trading:tradeList.quickActions.fullExit') : t('trading:tradeList.quickActions.partialClose'),
          onClick: () => handlers.quickAction(trade, isSl ? 'fullExit' : 'partialClose'),
        },
        {
          label: t('trading:inboxStudio.openPositions'),
          onClick: () => navigate('/app-test/trades'),
        },
      ],
    }
  }

  if (item.kind === 'near_sl') {
    const trade = item.data
    const distance = trade.distance ?? 0
    return {
      key: feedItemKey(item),
      subject: t('trading:dashboard.feedNearStopLossTitle', { symbol: trade.symbol }),
      preview: t('trading:dashboard.feedNearStopLossBody', {
        distance: distance.toFixed(2),
        stopLoss: fmtPrice(trade.stop_loss),
        currentPrice: fmtPrice(trade.current_price),
      }),
      body: t('trading:inboxStudio.nearSlBody', {
        symbol: trade.symbol,
        distance: distance.toFixed(2),
        stopLoss: fmtPrice(trade.stop_loss),
        currentPrice: fmtPrice(trade.current_price),
      }),
      date: trade.entry_date ? new Date(trade.entry_date) : null,
      unread: true,
      category: t('trading:inboxStudio.categoryRisk'),
      tone: 'warning',
      actions: [
        { label: t('trading:dashboard.feedActionViewTrade'), primary: true, onClick: () => handlers.openTrade(trade) },
        { label: t('trading:dashboard.feedActionEditSl'), onClick: () => handlers.quickAction(trade, 'sltp') },
        { label: t('trading:inboxStudio.openPositions'), onClick: () => navigate('/app-test/trades') },
      ],
    }
  }

  if (item.kind === 'missing_data') {
    const trade = item.data
    return {
      key: feedItemKey(item),
      subject: t('trading:dashboard.feedMissingDataTitle', { symbol: trade.symbol }),
      preview: t(getMissingDataBodyKey(trade)),
      body: t('trading:inboxStudio.missingDataBody', { symbol: trade.symbol }),
      date: trade.entry_date ? new Date(trade.entry_date) : null,
      unread: true,
      category: t('trading:inboxStudio.categoryJournal'),
      tone: 'warning',
      actions: [
        { label: t('trading:tradeList.quickActions.fullEdit'), primary: true, onClick: () => handlers.editTrade(trade) },
        { label: t('trading:dashboard.feedActionViewTrade'), onClick: () => handlers.openTrade(trade) },
        { label: t('trading:inboxStudio.openTrades'), onClick: () => navigate('/app-test/trades') },
      ],
    }
  }

  if (item.kind === 'after_exit_review') {
    const trade = item.data
    return {
      key: feedItemKey(item),
      subject: t('trading:dashboard.feedAfterExitReviewTitle', { symbol: trade.symbol }),
      preview: t('trading:dashboard.feedAfterExitReviewBody'),
      body: t('trading:inboxStudio.afterExitBody', {
        symbol: trade.symbol,
        exitDate: trade.exit_date
          ? new Date(trade.exit_date).toLocaleDateString(undefined, { dateStyle: 'medium' })
          : '—',
      }),
      date: trade.exit_date ? new Date(trade.exit_date) : null,
      unread: true,
      category: t('trading:inboxStudio.categoryReview'),
      tone: 'info',
      actions: [
        { label: t('trading:dashboard.feedActionAddReview'), primary: true, onClick: () => handlers.openTradeReview(trade) },
        { label: t('trading:dashboard.feedActionViewTrade'), onClick: () => handlers.openTrade(trade) },
        { label: t('trading:inboxStudio.openTrades'), onClick: () => navigate('/app-test/trades') },
      ],
    }
  }

  if (item.kind === 'add_first_strategy') {
    return {
      key: feedItemKey(item),
      subject: t('trading:dashboard.feedAddFirstStrategyTitle'),
      preview: t('trading:dashboard.feedAddFirstStrategyBody'),
      body: t('trading:inboxStudio.addStrategyBody'),
      date: null,
      unread: true,
      category: t('trading:inboxStudio.categorySetup'),
      tone: 'purple',
      actions: [
        { label: t('trading:dashboard.feedActionCreateStrategy'), primary: true, onClick: () => navigate('/app-test/strategies') },
        { label: t('trading:inboxStudio.openOverview'), onClick: () => navigate('/app-test') },
      ],
    }
  }

  if (item.kind === 'high_risk_exposure') {
    const criteriaSuffix = item.data.criteria
      ? t('trading:dashboard.feedHighRiskExposureCriteriaSuffix', { criteria: item.data.criteria })
      : ''
    return {
      key: feedItemKey(item),
      subject: t('trading:dashboard.feedHighRiskExposureTitle'),
      preview: t('trading:dashboard.feedHighRiskExposureBody', {
        riskPercent: item.data.riskPercent.toFixed(2).replace('.', ','),
        threshold: HIGH_RISK_EXPOSURE_THRESHOLD,
        criteriaSuffix,
      }),
      body: t('trading:inboxStudio.highRiskBody', {
        riskPercent: item.data.riskPercent.toFixed(1),
        threshold: HIGH_RISK_EXPOSURE_THRESHOLD,
        criteria: item.data.criteria || t('trading:inboxStudio.allOpenTrades'),
      }),
      date: new Date(),
      unread: true,
      category: t('trading:inboxStudio.categoryRisk'),
      tone: 'danger',
      actions: [
        { label: t('trading:inboxStudio.openOverview'), primary: true, onClick: () => navigate('/app-test') },
        { label: t('trading:inboxStudio.openPositions'), onClick: () => navigate('/app-test/trades') },
        { label: t('trading:inboxStudio.openAnalytics'), onClick: () => navigate('/app-test/analytics') },
      ],
    }
  }

  return {
    key: 'unknown',
    subject: 'Update',
    preview: '',
    body: '',
    date: null,
    unread: false,
    category: 'System',
    tone: 'neutral',
    actions: [],
  }
}
